import React from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { SelectField } from "checkout/ts/components/forms/Select";
import { Dispatch, StateShape } from "checkout/ts/redux/store";
import { FORM_ADDRESS, FORM_CHECKOUT_NAME } from "common/constants";
import { STATES_BY_COUNTRY } from "common/locale/states";
import { FormSection, getFormValues } from "redux-form";

import { Countries } from "../../../../../../common/locale/constants";
import { LOCALE_LABELS } from "../../../../locale/labels";
import { TextField } from "../../../forms/Text";
import { CountrySelect } from "../components/CountrySelect";

type OwnProps = {
    staticCountry?: keyof typeof Countries;
    isDark?: boolean;
    textFieldCustomStyles?: Record<string, string | Record<string, string>>;
    selectFieldCustomStyles?: Record<string, string | Record<string, string>>;
    invalidFields?: Record<string, { showField: boolean; error: string }>;
};

const stateSelector = (state: StateShape) => {
    const { inline } = state.application?.params?.params;
    const { data } = (getFormValues(inline ? FORM_CHECKOUT_NAME : FORM_ADDRESS)(state) || {}) as {
        data;
    };

    return { country: data?.country };
};

export const BillingAddressEn = ({
    staticCountry,
    isDark,
    textFieldCustomStyles = {},
    selectFieldCustomStyles = {},
    invalidFields = {},
}: OwnProps) => {
    const { formatMessage } = useIntl();

    const {
        application: { clearInvalidFieldError },
    } = useDispatch<Dispatch>();

    const { country } = useSelector(stateSelector);

    const states = STATES_BY_COUNTRY[country];

    return (
        <FormSection name="data" data-name="billing">
            <CountrySelect
                tabIndex={4}
                staticCountry={staticCountry}
                required
                isDark={isDark}
                fieldError={invalidFields.country?.error}
                onChange={() => clearInvalidFieldError({ name: "country" })}
                {...selectFieldCustomStyles}
            />

            <TextField
                label={formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_FIELDS_ZIP })}
                tabIndex={5}
                bsSize="sm"
                autoComplete="postal-code"
                name="zip"
                required
                isDark={isDark}
                fieldError={invalidFields.zip?.error}
                onChange={() => clearInvalidFieldError({ name: "zip" })}
                {...textFieldCustomStyles}
            />

            {states ? (
                <SelectField
                    label={formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_FIELDS_STATE })}
                    bsSize="sm"
                    autoComplete="state"
                    name="state"
                    tabIndex={6}
                    required
                    isDark={isDark}
                    fieldError={invalidFields.state?.error}
                    onChange={() => clearInvalidFieldError({ name: "state" })}
                    {...selectFieldCustomStyles}>
                    <option value="">{formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_FIELDS_STATE })}</option>

                    <option disabled>---</option>

                    {Object.keys(states).map((key) => (
                        <option key={key} value={states[key].name}>
                            {states[key].label || states[key].name}
                        </option>
                    ))}
                </SelectField>
            ) : (
                <TextField
                    label={formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_FIELDS_STATE })}
                    tabIndex={6}
                    bsSize="sm"
                    autoComplete="state"
                    name="state"
                    required
                    isDark={isDark}
                    fieldError={invalidFields.state?.error}
                    onChange={() => clearInvalidFieldError({ name: "state" })}
                    {...textFieldCustomStyles}
                />
            )}

            <TextField
                label={formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_FIELDS_CITY })}
                tabIndex={7}
                bsSize="sm"
                autoComplete="city"
                name="city"
                required
                isDark={isDark}
                fieldError={invalidFields.city?.error}
                onChange={() => clearInvalidFieldError({ name: "city" })}
                {...textFieldCustomStyles}
            />

            <TextField
                label={formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_FIELDS_LINE1 })}
                tabIndex={8}
                bsSize="sm"
                autoComplete="address-line-1"
                name="line1"
                required
                isDark={isDark}
                fieldError={invalidFields.line1?.error}
                onChange={() => clearInvalidFieldError({ name: "line1" })}
                {...textFieldCustomStyles}
            />

            <TextField
                label={formatMessage({ id: LOCALE_LABELS.FORM_ADDRESS_FIELDS_LINE2 })}
                tabIndex={9}
                bsSize="sm"
                autoComplete="address-line-2"
                name="line2"
                isDark={isDark}
                {...textFieldCustomStyles}
            />
        </FormSection>
    );
};
